import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import useFilters from '../../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../../components/Filter/components/FilterInput';
import useDebounce from '../../../../components/hooks/useDebounce';
import { translate } from '../../../../services/translate';
import Filter from './Filters';
import { FlatList } from 'react-native-web-hover';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import Toast from 'react-native-toast-message';
import MyAppText from '../../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '../../../../components/Tooltip';
import { appAgentService } from '../../../../services/central-api/app-agent';
import VacancyCard from './VacancyCard';
import moment from 'moment';
import { AppAgentParamList } from '../../../../typings/Params';
import { useAuth } from '../../../../context/Auth';
import { RootNavigation } from '../../../../Routes';

export default function OpenVacancies({ navigation }: { navigation: RootNavigation; }) {
    const { styles, theme } = useStyles(styleSheet);
    const route = useRoute<RouteProp<AppAgentParamList, 'OpenVacancies'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AppAgentParamList['OpenVacancies'] & {
        page: number;
        limit: number;
    }>({
        ...route.params,
        page: 0,
        limit: 25,
    });

    const [textFilter, setTextFilter] = useState<string>(filters.textFilter ?? '');
    const debouncedFilter = useDebounce(textFilter, 750);
    const [vacancies, setVacancies] = useState<VacancyWithErrors[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [lastVacanciesFetch, setLastVacanciesFetch] = useState<number>(0);

    async function getVacancies() {
        try {
            setIsLoading(true);
            const response = await appAgentService.getVacancies(filters);
            if (filters.page == 0) {
                setVacancies(response.rows);
            } else {
                setVacancies([...vacancies, ...response.rows.filter(v => !vacancies.map(v => v.vacancy.id).includes(v.vacancy.id))]);
            }
            setTotalItems(response.count);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { page, limit, ...others } = filters;
            navigation.setParams({
                ...others,
            });

            if (filters.page === 0) {
                setVacancies([]);
                setTotalItems(0);
            }

            getVacancies();
        }
    }, [isFocused, filters, navigation]));

    useEffect(() => {
        setFilters(old => ({
            ...old,
            page: 0,
            textFilter: textFilter !== '' ? textFilter : undefined,
        }));
    }, [debouncedFilter]);

    function onEndReached() {
        if (vacancies.length < totalItems && !isLoading) {
            const lastFetchInterval = moment().diff(moment(lastVacanciesFetch), 'seconds');
            if (lastVacanciesFetch === 0 || lastFetchInterval > 10) {
                setFilters(old => ({
                    ...old,
                    page: old.page + 1
                }));
                setLastVacanciesFetch(moment().valueOf());
            }
        }
    }

    function listFooterComponent() {
        if (isLoading) {
            if (vacancies.length === 0) {
                return <View style={{ flex: 1, justifyContent: 'center' }}>
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                </View>;
            }
            return <ActivityIndicator size='large' color={theme.colors.loadingColor} />;
        }
    }

    function listEmptyComponent() {
        if (!isLoading && vacancies.length === 0) {
            return (
                <View style={{ flex: 1, justifyContent: 'center' }}>
                    <MyAppText style={{ textAlign: 'center' }}>{
                        !authenticatedUser?.agentSchedule
                            ? translate('withoutDefinedWorkSchedule')
                            : translate('thereIsNoVacanciesInsidePeriod')
                    }</MyAppText>
                </View>
            );
        }
    }

    return (
        <View style={styles.container(UnistylesRuntime.themeName == 'dark')}>
            <View style={styles.header}>
                <View style={styles.headerFilters}>
                    <View style={{ flex: 1 }}>
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                        />
                    </View>
                    <Filter
                        filters={filters}
                        setFilters={setFilters}
                        setTextFilter={setTextFilter}
                        textFilter={textFilter}
                    />
                </View>
            </View>
            <View style={styles.content}>
                <View style={{ zIndex: 2, flexDirection: 'row', columnGap: 8, alignItems: 'baseline' }}>
                    <MyAppText style={styles.contentTitle}>{translate('openVacancies')} ({totalItems})</MyAppText>
                    <Tooltip
                        useSimpleBox
                        style={{
                            color: theme.colors.white['500'],
                            padding: 16,
                            borderRadius: 12,
                            justifyContent: 'center',
                            zIndex: 9999,
                            background: theme.colors.neutral['500'],
                        }}
                        tooltipContent={translate('vacanciesCriteriaHintMessage')}
                        tooltipDirection='right'
                    >
                        <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                    </Tooltip>
                </View>
                <FlatList
                    showsVerticalScrollIndicator={false}
                    style={{ flex: 1 }}
                    data={vacancies}
                    onEndReached={onEndReached}
                    renderItem={(item) =>
                        <VacancyCard
                            vacancy={item.item as VacancyWithErrors}
                            onPress={() => {
                                setIsLoading(true);
                                setFilters({
                                    ...filters,
                                    page: 0,
                                });
                                navigation.navigate('AppAgent', { screen: 'VacancyDetailsApply', params: { id: (item.item as VacancyWithErrors).vacancy.id } });
                            }}
                        />
                    }
                    keyExtractor={(item, index) => `${item}-${index}`}
                    contentContainerStyle={{ flex: 1, gap: 24, paddingHorizontal: 10, paddingTop: 10 }}
                    ListFooterComponentStyle={{ flex: isLoading ? 1 : 0 }}
                    ListFooterComponent={listFooterComponent}
                    ListEmptyComponent={listEmptyComponent}
                />
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: (isDark: boolean) => ({
        flex: 1,
        paddingTop: 24,
        gap: 24,
        backgroundColor: isDark ? 'transparent' : theme.colors.backgrounds['BG-2']
    }),
    header: {
        flexDirection: 'column',
        rowGap: 8,
        zIndex: 3
    },
    headerFilters: {
        flexDirection: 'row',
        columnGap: 8,
        zIndex: 2,
        paddingHorizontal: 16,
    },
    content: {
        paddingHorizontal: 16,
        flex: 1,
        flexDirection: 'column',
        rowGap: 16,
    },
    contentTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    }
}));
